import { action, computed, observable } from 'mobx';
import { setter, toggle } from 'mobx-decorators';
import { deserializeWithMappers, setFieldValues } from 'stores/Utils';
import ContentStore from './ContentStore';
import DiscountStore from './DiscountStore';
import pageStore from './PageStore';
import * as constants from 'Constants';
import * as utils from 'Utils';


class AovProgressBarStore {

  @setter @observable id;
  @setter @observable platform;
  @setter @observable placement;
  @setter @observable placementCssSelector;
  @setter @observable placementInsertPosition;
  @setter @observable content;
  @setter @observable cssStyles;
  @setter @observable customCssStyles;
  @setter @observable cssClasses;

  @setter @observable el;
  @setter @toggle @observable ready;

  constructor() {
    this.reset();
  }

  @action reset() {
    this.id = null;
    this.platform = "";
    this.placement = "";
    this.placementCssSelector = "";
    this.placementInsertPosition = "";
    this.content = new ContentStore();
    this.cssStyles = "";
    this.customCssStyles = "";
    this.cssClasses = [];

    this.el = null;
    this.ready = false;
  }

  getDeserializeMappers() {
    return [
      {"field": "id"},
      {"field": "platform"},
      {"field": "placement"},
      {"field": "placementCssSelector", "accessor": "placement_css_selector"},
      {"field": "placementInsertPosition", "accessor": "placement_insert_position"},
      {"field": "content", "accessor": "style_config.content", "type": constants.DESERIALIZE_TYPE_OBJECT, "cls": ContentStore},
      {"field": "cssStyles", "accessor": "css_styles"},
      {"field": "customCssStyles", "accessor": "style_config.css.custom_css_styles"},
      {"field": "cssClasses", "accessor": "style_config.css.css_classes"},
    ];
  }

  @action async deserialize(data) {
    await deserializeWithMappers(this, this.getDeserializeMappers(), data);
    this.ready = true;
  }

  @action setFields(data) {
    let self = this;
    for (var key in data) {
      let value = data[key];
      self[key] = value;
    }
  }

  @computed get selectorPath() {
    return `.ov-app-aov-progress-bar[data-app-id="${this.id}"]`;
  }

  @computed get selector() {
    return document.querySelector(this.selectorPath);
  }

  @computed get isSelectorPlacement() {
    return this.placement === "PRODUCT_PAGE_SELECTOR";
  }

  @computed get insertPositionBefore() {
    return this.placementInsertPosition === "BEFORE";
  }

  @computed get insertPositionAfter() {
    return this.placementInsertPosition === "AFTER";
  }

  @computed get isShopify() {
    return this.platform === "shopify";
  }

  @computed get isBigCommerce() {
    return this.platform === "bigcommerce";
  }

  @computed get isEasyStore() {
    return this.platform === "easystore";
  }

  @computed get isShopware() {
    return this.platform === "shopware";
  }

  @computed get isWix() {
    return this.platform === "wix";
  }

  @computed get isWooCommerce() {
    return this.platform === "woocommerce";
  }

  @computed get isOtherPlatforms() {
    return this.platform === "others";
  }

  @computed get display() {
    if (!this.content.discounts.length) return false;
    return true;
  }

  @computed get currentDiscount() {
    const content = this.content;
    const discount = content.discounts.filter((discount) => discount.minAmount > pageStore.cartAmount)[0];
    return discount;
  }

  @computed get eligibleDiscounts() {
    const content = this.content;
    const discounts = content.discounts.filter((discount) => pageStore.cartAmount >= discount.minAmount);
    return discounts;
  }

  @computed get eligibleDiscountAmount() {
    const amounts = this.eligibleDiscounts.map((discount) => discount.minAmount);
    if (amounts.length === 0) return 0;
    return Math.max(...amounts);
  }

  @computed get requiredAmount() {
    const currentDiscount = this.currentDiscount;
    if (!currentDiscount) return;
    return currentDiscount.minAmount - pageStore.cartAmount;
  }

  @computed get barPct() {
    if (!pageStore.cartAmount) return 0;
    if (!this.currentDiscount) return 100;
    if (!this.requiredAmount) return 100;

    const denominator = this.currentDiscount.minAmount-this.eligibleDiscountAmount;
    const numerator = (this.requiredAmount-denominator)*-1;

    return (numerator/denominator) * 100;
  }

  @computed get variables() {
    const data = {}
    const currentDiscount = this.currentDiscount;
    if (!currentDiscount) return data;

    let requiredAmountDisplay = utils.formatAmountDisplay(this.requiredAmount);

    if (pageStore.formatAmountDisplay) {
      try {
        requiredAmountDisplay = pageStore.formatAmountDisplay(this.requiredAmount);
      } catch (error) {
        console.error(error);
      }
    }

    data['required_amount'] = requiredAmountDisplay;
    return data;
  }

  @computed get completionNoteHtml() {
    return this.content.completionNoteHtml;
  }

  @computed get ctaHtml() {
    const currentDiscount = this.currentDiscount;
    if (!currentDiscount) return;
    return utils.liquidRender(currentDiscount.ctaHtml, this.variables);
  }

  @computed get completed() {
    if (!this.eligibleDiscounts.length) return false;
    return this.content.discounts.length === this.eligibleDiscounts.length;
  }
}

export default AovProgressBarStore;
